import { _js } from '@ifixit/utils/localize';
import { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import { color } from '@ifixit/primitives';
import AccordionItem from './accordion_item';
import AccordionSubitem from './accordion_subitem';
import LanguageButton from '../dropdowns/language_button';
import LanguageDropdown from '../dropdowns/language_dropdown';
import { useMediaQuery } from 'react-responsive';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { HeadingLinkType, SublinkType } from 'Shared/Header/links-types';
import { useTranslations } from '@ifixit/i18n/hooks';
import { FaIcon } from '@ifixit/ui/components/icon';
import { LocationPicker } from '@ifixit/ui/components/location-picker';
import { canAccessWorkbench, WORKBENCH_PATH, type WorkbenchUser } from '@ifixit/workbench';
import * as HeaderActions from 'Shared/Header/actions';

const navHeight = '68px';

const Container = styled.div<{ $isOpen?: boolean }>`
   position: absolute;
   padding: 32px 16px 0;
   top: ${navHeight};
   left: 0;
   right: 0;
   height: calc(100vh - ${navHeight});
   background: ${color.gray[900]};
   opacity: 0;
   visibility: hidden;
   transform: translateX(-100%);
   transition: opacity 0.25s, transform 0.25s;
   overflow-x: hidden;
   overflow-y: auto;
   -webkit-overflow-scrolling: touch;
   z-index: 200;

   ${props =>
      props.$isOpen &&
      css`
         opacity: 1;
         visibility: visible;
         transform: translateX(0);
      `};
`;

const mapStateToProps = (state: any) => ({
   headerState: state.headerState,
});

const mapDispatchToProps = {
   closeMobileMenu: HeaderActions.closeMobileMenu,
};

const regionAccordionTitle = _js('Language / Region');

const MobileMenu = ({
   headerState,
   links,
   languageDropdown,
   isOnCart,
   onWorkbenchOpen,
   workbenchUser,
   closeMobileMenu,
}: {
   headerState: any;
   links: HeadingLinkType[];
   languageDropdown: any;
   isOnCart: boolean;
   onWorkbenchOpen: () => void;
   workbenchUser?: WorkbenchUser | null;
   closeMobileMenu: () => void;
}) => {
   const { displayMobileMenu } = headerState;

   const [activeId, setActiveId] = useState();

   const accordionItemClicked = useCallback(
      (clickedId: any) => {
         setActiveId(activeId === clickedId ? null : clickedId);
      },
      [activeId, setActiveId]
   );

   const { languages, translationPreferencesUrl } = languageDropdown;

   const selectedLanguage = languages.find((language: any) => language.isSelected);

   const fullWidthDropdown = useMediaQuery({ maxWidth: '450px' });
   const t = useTranslations('Workbench');
   const hasWorkbenchAccess =
      App.siteName === 'ifixit' && workbenchUser != null && canAccessWorkbench(workbenchUser);

   return (
      <Container id="mobileMenuContainer" $isOpen={displayMobileMenu}>
         {links.map(
            (data: any) =>
               data.disabled || (
                  <AccordionItem
                     key={`accordion-item-${data.title}`}
                     title={data.title}
                     isActive={data.title === activeId}
                     onClick={accordionItemClicked}
                  >
                     {data.sublinks.map(
                        (sublink: SublinkType, index: any) =>
                           sublink.disabled || (
                              <AccordionSubitem
                                 href={sublink.url}
                                 key={`accordion-${data.title}-sublink-${index}`}
                                 title={sublink.title}
                              />
                           )
                     )}
                  </AccordionItem>
               )
         )}
         {App.siteName !== 'ifixit' && (
            <AccordionItem
               title={regionAccordionTitle}
               isActive={regionAccordionTitle === activeId}
               onClick={accordionItemClicked}
            >
               {!isOnCart && (
                  <AccordionSubitem
                     title={selectedLanguage.autoglottonym}
                     icon={<LanguageButton isoCode={selectedLanguage.isoCode} />}
                     fullWidthDropdown={fullWidthDropdown}
                     dropdown={
                        <LanguageDropdown
                           translationPreferencesUrl={translationPreferencesUrl}
                           languages={languages}
                           isLightTheme
                           mobile={fullWidthDropdown}
                        />
                     }
                     onMount={() => {
                        trackAnalyticsCustomEvent({
                           eventCategory: 'Language',
                           eventAction: 'Language Dropdown - Click',
                        });
                     }}
                  />
               )}
            </AccordionItem>
         )}
         <div className="px-4 mt-8">
            {hasWorkbenchAccess ? (
               <a
                  href={WORKBENCH_PATH}
                  className="flex w-full cursor-pointer items-center py-3 text-gray-300 text-base transition-opacity hover:opacity-70"
                  id="workbenchDropdownToggle"
               >
                  <FaIcon name="toolbox" className="size-6 text-white mr-3" />
                  {t('workbench')}
               </a>
            ) : (
               <button
                  type="button"
                  onClick={onWorkbenchOpen}
                  className="flex w-full cursor-pointer items-center py-3 text-gray-300 text-base transition-opacity hover:opacity-70"
                  id="workbenchDropdownToggle"
               >
                  <FaIcon name="toolbox" className="size-6 text-white mr-3" />
                  {t('workbench')}
               </button>
            )}
            {App.siteName === 'ifixit' && (
               <LocationPicker
                  LinkComponent="a"
                  trigger={
                     <button
                        type="button"
                        onClick={closeMobileMenu}
                        className="flex w-full cursor-pointer items-center py-3 text-gray-300 text-base transition-opacity hover:opacity-70"
                     >
                        <FaIcon name="globe" style="regular" className="size-6 text-white mr-3" />
                        {regionAccordionTitle}
                     </button>
                  }
               />
            )}
         </div>
      </Container>
   );
};

export default connect(mapStateToProps, mapDispatchToProps)(MobileMenu);
